/**
 * URL-scheme safety helper — shared between views that render backend-controlled
 * URLs into `<a href="...">` attributes.
 *
 * `esc()` escapes HTML delimiters but NOT URL schemes — an attacker-controlled
 * `javascript:` or `data:text/html,...` URL passes `esc()` unchanged and yields
 * one-click XSS when interpolated into `href`. Use this helper at every
 * boundary that accepts an external URL (SEC-1).
 *
 * Accepts relative URLs (`/`, `#`, `./`, `../`) for in-app navigation. Returns
 * the URL unchanged if safe; empty string otherwise so callers can render a
 * placeholder or omit the anchor.
 */
// Browser-only helper (uses `window.location.origin`). No Workers / SSR callers today.
export function safeHttpUrl(url: string | null | undefined): string {
  const raw = (url ?? '').trim()
  if (!raw) {
    return ''
  }
  // Reject protocol-relative URLs BEFORE the in-app-relative branch — browsers
  // resolve `//evil.com/x` to `https://evil.com/x`, which is an open-redirect
  // / phishing vector even though no script executes (M-1, R1).
  if (raw.startsWith('//')) {
    return ''
  }
  if (raw.startsWith('/') || raw.startsWith('#') || raw.startsWith('./') || raw.startsWith('../')) {
    return raw
  }
  try {
    const p = new URL(raw, window.location.origin)
    return p.protocol === 'http:' || p.protocol === 'https:' ? raw : ''
  } catch {
    return ''
  }
}
